import Input from 'components/Input';
import {
  JcapAsyncReactSelect,
  JcapReactSelect,
  makeJcapReactSelectProps,
} from 'components/JcapReactSelect/JcapReactSelect';
import {
  SearchBanksQuery,
  SearchBanksQueryVariables,
  useSearchBanksQuery,
} from 'graphql/queries/SearchBanks.generated';
import {
  SearchBusinessUnitsQuery,
  SearchBusinessUnitsQueryVariables,
  useSearchBusinessUnitsQuery,
} from 'graphql/queries/SearchBusinessUnits.generated';
import {
  SearchCurrenciesQuery,
  SearchCurrenciesQueryVariables,
  useSearchCurrenciesQuery,
} from 'graphql/queries/SearchCurrencies.generated';
import {
  SearchCurrencyBooksQuery,
  SearchCurrencyBooksQueryVariables,
  useSearchCurrencyBooksQuery,
} from 'graphql/queries/SearchCurrencyBooks.generated';
import {
  SearchOrganisationsQuery,
  SearchOrganisationsQueryVariables,
  useSearchOrganisationsQuery,
} from 'graphql/queries/SearchOrganisations.generated';
import React from 'react';
import { declareMakeMutationForm } from 'toolbox';
import { Checkbox } from 'toolbox';

export const makeMutationForm = declareMakeMutationForm({
  inputs: {
    text: ({ initialError, initialTouched, initialValue, ...props }) => (
      <Input type="text" {...props} />
    ),
    hidden: () => null,
    checkbox: ({ initialError, initialTouched, initialValue, ...props }) => (
      <Checkbox id="a" checked={Boolean(props.value)} {...props} />
    ),
    businessUnitId: (props) => (
      <JcapAsyncReactSelect<
        SearchBusinessUnitsQueryVariables,
        SearchBusinessUnitsQuery,
        SearchBusinessUnitsQuery['business_units'][0]
      >
        {...props}
        {...makeJcapReactSelectProps(props)}
        useQuery={useSearchBusinessUnitsQuery}
        resultAccessor={(result) => result.data?.business_units || []}
        valueAccessor={({ id }) => id}
        labelAccessor={({ name }) => name}
        makeVariablesFromInput={(search) => ({
          where: { name: { _ilike: `%${search}%` } },
        })}
      />
    ),
    select: (props) => <JcapReactSelect {...props} />,
    currencyId: (props) => (
      <JcapAsyncReactSelect<
        SearchCurrenciesQueryVariables,
        SearchCurrenciesQuery,
        SearchCurrenciesQuery['currencies'][0]
      >
        {...props}
        {...makeJcapReactSelectProps(props)}
        useQuery={useSearchCurrenciesQuery}
        resultAccessor={(result) => result.data?.currencies}
        valueAccessor={({ id }) => id}
        labelAccessor={({ name, prefix }) => `${prefix} - ${name}`}
        makeVariablesFromInput={(search) => ({
          where: {
            _or: [
              { name: { _ilike: `%${search}%` } },
              { prefix: { _ilike: `%${search}%` } },
            ],
          },
        })}
      />
    ),
    organisation_id: (props) => (
      <JcapAsyncReactSelect<
        SearchOrganisationsQueryVariables,
        SearchOrganisationsQuery,
        SearchOrganisationsQuery['organisations'][0]
      >
        {...props}
        {...makeJcapReactSelectProps(props)}
        useQuery={useSearchOrganisationsQuery}
        resultAccessor={(result) => result.data?.organisations || []}
        valueAccessor={({ id }) => id}
        labelAccessor={({ name }) => name}
        makeVariablesFromInput={(search) => ({
          where: { name: { _ilike: `%${search}%` } },
        })}
      />
    ),
    bank_id: (props) => (
      <JcapAsyncReactSelect<
        SearchBanksQueryVariables,
        SearchBanksQuery,
        SearchBanksQuery['banks'][0]
      >
        {...props}
        {...makeJcapReactSelectProps(props)}
        useQuery={useSearchBanksQuery}
        resultAccessor={(result) => result.data?.banks || []}
        valueAccessor={({ id }) => id}
        labelAccessor={({ name }) => name}
        makeVariablesFromInput={(search) => ({
          // limit: 10,
          where: { name: { _ilike: `%${search}%` } },
          order_by: { name: 'asc' },
        })}
      />
    ),
    currency_book_id: (props) => (
      <JcapAsyncReactSelect<
        SearchCurrencyBooksQueryVariables,
        SearchCurrencyBooksQuery,
        SearchCurrencyBooksQuery['currency_books'][0]
      >
        {...props}
        {...makeJcapReactSelectProps(props)}
        useQuery={useSearchCurrencyBooksQuery}
        resultAccessor={(result) => result.data?.currency_books || []}
        valueAccessor={({ id }) => id}
        labelAccessor={({ name }) => name}
        makeVariablesFromInput={(search) => ({
          where: { name: { _ilike: `%${search}%` } },
        })}
      />
    ),
    bankAccountProductType: (props) => (
      <JcapReactSelect
        {...props}
        {...makeJcapReactSelectProps(props)}
        valueAccessor={({ value }) => value}
        labelAccessor={({ label }) => label}
        options={[
          {
            value: 'CALL',
            label: 'Call',
          },
          {
            value: 'NOTICE',
            label: 'Notice',
          },
          {
            value: 'FIXED',
            label: 'Fixed',
          },
        ]}
      />
    ),
    // TODO - improve
    date: ({ initialError, initialTouched, initialValue, ...props }) => {
      return <Input type="date" {...props} />;
    },

    email: ({
      initialError,
      initialValue,
      initialTouched,
      touched,
      ...props
    }) => <Input type="email" {...props} />,

    password: ({
      initialError,
      initialValue,
      initialTouched,
      touched,
      ...props
    }) => <Input type="password" {...props} />,

    number: ({ initialError, initialTouched, initialValue, ...props }) => {
      const inputFilter = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (
          (event.keyCode <= 46 && event.keyCode !== 32) ||
          event.ctrlKey ||
          event.metaKey
        )
          return; // allow non-char keys (other than space)
        if (event.key.match(/[^\d.-]/g)) event.preventDefault(); // numbers and decimals only
        if (event.key === '.' && event.currentTarget.value.match(/.*\..*/g))
          event.preventDefault(); // decimal can't exist more than once
        if (event.key === '-' && event.currentTarget.selectionStart > 0)
          event.preventDefault(); // only allow minus symbol as the first char
      };
      return (
        <Input
          type="text"
          inputMode="numeric"
          onKeyDown={(e) => inputFilter(e)}
          placeholder="0.00"
          {...props}
        />
      );
    },
  },
});
